import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ReviewsSection = () => {
  const { language } = useLanguage();
  const [reviews, setReviews] = useState([]);
  const [allReviews, setAllReviews] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);

  const translations = {
    lv: {
      title: 'Atsauksmes',
      subtitle: 'Ko saka mūsu viesi',
      noReviews: 'Pagaidām nav atsauksmju',
      leaveReview: 'Atstāt atsauksmi',
      showAll: 'Skatīt visas',
      close: 'Aizvērt',
      basedOn: 'pamatojoties uz',
      reviews: 'atsauksmēm',
      allReviews: 'Visas atsauksmes'
    },
    en: {
      title: 'Reviews',
      subtitle: 'What our guests say',
      noReviews: 'No reviews yet',
      leaveReview: 'Leave a review',
      showAll: 'Show all',
      close: 'Close',
      basedOn: 'based on',
      reviews: 'reviews',
      allReviews: 'All reviews'
    },
    ru: {
      title: 'Отзывы',
      subtitle: 'Что говорят наши гости',
      noReviews: 'Пока нет отзывов',
      leaveReview: 'Оставить отзыв',
      showAll: 'Показать все',
      close: 'Закрыть',
      basedOn: 'на основе',
      reviews: 'отзывов',
      allReviews: 'Все отзывы'
    }
  };

  const t = translations[language] || translations.lv;

  useEffect(() => {
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/public/reviews`);
        if (response.ok) {
          const data = await response.json();
          setReviews(data.reviews || []);
          setAllReviews(data.allReviews || data.reviews || []);
          setStats(data.stats || null);
        }
      } catch (error) {
        console.error('Failed to fetch reviews:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchReviews();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'lv' ? 'lv-LV' : language === 'ru' ? 'ru-RU' : 'en-US', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return null;
  }

  return (
    <section 
      id="reviews" 
      className="py-24 md:py-32 bg-white"
      data-testid="reviews-section"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="text-[#8B2942] uppercase tracking-[0.3em] text-sm">
            {t.subtitle}
          </span>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl text-[#1A1A1D] mt-4">
            {t.title}
          </h2>
          <div className="section-divider mt-8" />
          
          {/* Stats */}
          {stats && stats.total > 0 && (
            <div className="flex items-center justify-center gap-2 mt-6">
              <div className="flex gap-1">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star
                    key={star}
                    size={24}
                    className={
                      star <= Math.round(stats.average)
                        ? 'text-[#D4AF37] fill-[#D4AF37]'
                        : 'text-gray-300'
                    }
                  />
                ))}
              </div>
              <span className="text-2xl font-bold text-[#1A1A1D] ml-2">{stats.average}</span>
              <span className="text-[#6B6B6B] ml-2">
                {t.basedOn} {stats.total} {t.reviews}
              </span>
            </div>
          )}
        </div>

        {/* Reviews Grid */}
        {reviews.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
            {reviews.slice(0, 6).map((review) => (
              <div 
                key={review.id} 
                className="bg-[#FAF9F6] p-6 rounded-lg"
                data-testid={`review-card-${review.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star
                      key={star}
                      size={18}
                      className={
                        star <= review.rating
                          ? 'text-[#D4AF37] fill-[#D4AF37]'
                          : 'text-gray-300'
                      }
                    />
                  ))}
                </div>
                {review.comment && (
                  <p className="text-[#1A1A1D] mb-4 line-clamp-4">
                    "{review.comment}"
                  </p>
                )}
                <p className="text-[#9CA3AF] text-sm">
                  {formatDate(review.created_at)}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-[#6B6B6B]">{t.noReviews}</p>
          </div>
        )}

        {/* CTA Button */}
        <div className="text-center flex flex-col sm:flex-row gap-4 justify-center">
          {stats && stats.total > 6 && (
            <button
              onClick={() => setShowAll(true)}
              className="inline-block px-8 py-4 bg-[#8B2942] text-white font-medium tracking-wider uppercase hover:bg-[#6B1F32] transition-all duration-300"
              data-testid="show-all-reviews-btn"
            >
              {t.showAll} ({stats.total})
            </button>
          )}
          <a
            href="/review"
            className="inline-block px-8 py-4 border-2 border-[#8B2942] text-[#8B2942] font-medium tracking-wider uppercase hover:bg-[#8B2942] hover:text-white transition-all duration-300"
            data-testid="leave-review-btn"
          >
            {t.leaveReview}
          </a>
        </div>
      </div>

      {/* Modal for all reviews */}
      {showAll && (
        <div 
          className="fixed inset-0 z-[200] bg-black/80 flex items-center justify-center p-4"
          onClick={() => setShowAll(false)}
        >
          <div 
            className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h3 className="font-['Playfair_Display'] text-2xl text-[#1A1A1D]">
                  {t.allReviews}
                </h3>
                {stats && (
                  <div className="flex items-center gap-2 mt-2">
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <Star
                          key={star}
                          size={16}
                          className={
                            star <= Math.round(stats.average)
                              ? 'text-[#D4AF37] fill-[#D4AF37]'
                              : 'text-gray-300'
                          }
                        />
                      ))}
                    </div>
                    <span className="text-[#6B6B6B] text-sm">
                      {stats.average} ({stats.total} {t.reviews})
                    </span>
                  </div>
                )}
              </div>
              <button
                onClick={() => setShowAll(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={24} className="text-[#6B6B6B]" />
              </button>
            </div>
            
            {/* Modal Body */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              <div className="grid gap-4">
                {allReviews.map((review) => (
                  <div 
                    key={review.id} 
                    className="bg-[#FAF9F6] p-5 rounded-lg"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex gap-1">
                        {[1, 2, 3, 4, 5].map((star) => (
                          <Star
                            key={star}
                            size={18}
                            className={
                              star <= review.rating
                                ? 'text-[#D4AF37] fill-[#D4AF37]'
                                : 'text-gray-300'
                            }
                          />
                        ))}
                      </div>
                      <span className="text-[#9CA3AF] text-sm">
                        {formatDate(review.created_at)}
                      </span>
                    </div>
                    {review.comment && (
                      <p className="text-[#1A1A1D]">"{review.comment}"</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default ReviewsSection;
